import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { getTemplateDetails } from "../api";
import MainSpinner from "../components/MainSpinner";
import {
  BiFolderPlus,
  BiHeart,
  BiSolidFolderPlus,
  BiSolidHeart,
} from "react-icons/bi";
import useUser from "../hooks/useUser";
import { toast } from "react-toastify";
import apis from "../apis/apis";
import useTemplates from "../hooks/useTemplates";
import { AnimatePresence } from "framer-motion";
import { TemplateDesignPin } from "../components";

const TemplateDesignPinDetails = () => {
  const { templateId } = useParams();
  const { data: user, refetch: userRefetch } = useUser();
  const {
    data: templates,
    refetch: temp_refetch,
    isLoading: temp_isLoading,
  } = useTemplates();

  // ✅ Add to Collections
  const addToCollection = async (e) => {
    e.stopPropagation();

    try {
      const userId =  user?.uid;
      if (!userId) return toast.error("User not authenticated");
      if (!data?._id) return toast.error("Invalid template data");
      console.log(userId)

      const { data: res } = await apis.post("/saveToCollections", {
        uid: userId,
        dataId: data._id,
      });

      toast.success(res.message);
      userRefetch();
    } catch (err) {
      console.error("Error adding to collection:", err);
      toast.error(`Error: ${err.response?.data?.message || err.message}`);
    }
  };

  // ✅ Save to Favourites
  const saveToFavourites = async (e) => {
    e.stopPropagation();

    try {
      const userId = user?._id || user?.uid;
      if (!userId) return toast.error("User not authenticated");
      if (!data?._id) return toast.error("Invalid template data");

      const { data: res } = await apis.post("/saveToFavourites", {
        userId,
        templateId: data._id,
      });

      toast.success(res.message);
      await Promise.all([temp_refetch(), refetch()]);
    } catch (err) {
      console.error("Error saving to favourites:", err);
      toast.error(`Error: ${err.response?.data?.error || err.message}`);
    }
  };

  // ✅ Fetch template details
  const {
    data,
    isError,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["template", templateId],
    queryFn: () => getTemplateDetails(templateId),
    enabled: !!templateId,
  });

  if (isLoading) return <MainSpinner />;

  if (isError) {
    return (
      <div className="w-full h-[60vh] flex flex-col items-center justify-center text-red-500">
        <p className="text-lg text-txtPrimary font-semibold">
          Error loading template...
        </p>
      </div>
    );
  }

  return (
  <div className="w-full max-w-[1400px] mx-auto py-10 px-6" style={{ marginTop: "50px" }}>

    {/* ===== Canvas Row ===== */}
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">

      {/* === Preview Area === */}
      <div className="col-span-1 lg:col-span-8 flex flex-col gap-4">
        <img
          src={data?.imageUrl}
          alt={data?.title}
          className="w-full rounded-lg object-contain shadow-sm"
        />
      </div>

      {/* === Sidebar === */}
      <div className="col-span-1 lg:col-span-4 flex flex-col gap-8">

        {/* Title + Likes */}
        <div className="flex flex-col gap-3">
          <h1 className="text-3xl font-semibold text-gray-900">
            {data?.title}
          </h1>

          {data?.favourites?.length > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <BiSolidHeart className="text-red-500" />
              <span className="text-gray-700 font-medium">
                {data.favourites.length} likes
              </span>
            </div>
          )}
        </div>

        {/* Action buttons vertical */}
        {user && (
          <div className="flex flex-col gap-3">

            {/* Save to collections */}
            <button
              onClick={addToCollection}
              className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition w-full"
            >
              {user?.collections?.includes(data?._id)
                ? <><BiSolidFolderPlus /> Remove from Collection</>
                : <><BiFolderPlus /> Save to Collection</>
              }
            </button>

            {/* Favourites */}
            <button
              onClick={saveToFavourites}
              className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition w-full"
            >
              {data?.favourites?.includes(user?._id)
                ? <><BiSolidHeart /> Remove from Favourites</>
                : <><BiHeart /> Add to Favourites</>
              }
            </button>

            {/* Edit CTA */}
            <Link
              to={`/homescreen/resume/${data?.name}?templateId=${templateId}`}
              className="mt-2 px-5 py-3 text-center text-white bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold w-full"
            >
              Edit this template
            </Link>
            </div>
          )}

          {/* Tags */}
        {data?.tags && (
          <div className="flex flex-wrap gap-2">
            {data.tags.split(",").map((tag, i) => (
              <span
                key={i}
                className="text-xs px-3 py-1 bg-gray-100 border border-gray-200 text-gray-600 rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

    </div>

    {/* ===== Similar Templates ===== */}
    {templates?.filter(t => t._id !== data?._id)?.length > 0 && (
      <div className="mt-14 flex flex-col gap-6">
        <h2 className="text-xl font-semibold text-gray-900">
          You might also like
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6">
          <AnimatePresence>
            {templates
              ?.filter(t => t._id !== data?._id)
              .map((template, index) => (
                <TemplateDesignPin
                  key={template._id}
                  data={template}
                  index={index}
                />
              ))}
          </AnimatePresence>
        </div>
      </div>
    )}

  </div>
); 
};

export default TemplateDesignPinDetails;